<script lang="ts">
	import {
		Label,
		LabelV2,
		Stack,
		Icon,
		Dots,
		ControlsContainer,
		type LabelV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { type DotsPropsTypes } from '@Shelly/shelly-design-system';
	import { beforeUpdate } from 'svelte';
	import { mappedInputReaderEvent } from 'src/helpers/input_reader_helpers';
	import RotationIndicator from './RotationIndicator.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const MILLISECONDS_TO_SECONDS = 1000;
	const RECENT_EVENT_THRESHOLD_SECONDS = 10;
	const MAX_BUTTON_COUNT = 2;

	export let buttonEvents = [];
	export let rotationData;
	export let useV2Components = false;
	export let labelV2Props: Partial<LabelV2PropsTypes> = {};
	export let rotaryOnly = false;

	let buttonColors: DotsPropsTypes['color'][] = ['default', 'default'];
	let rotationColor: DotsPropsTypes['color'] = 'default';

	let currentTimestamp = null;

	/**
	 * Calculates the time difference in seconds between the current time and an event timestamp.
	 * @param eventTimestamp - The timestamp of the event in milliseconds
	 * @param currentTime - The current timestamp in seconds
	 * @returns The absolute time difference in seconds
	 */
	const calculateTimeDifference = (eventTimestamp: number, currentTime: number): number => {
		return Math.abs(currentTime - Math.floor(eventTimestamp / MILLISECONDS_TO_SECONDS));
	};

	/**
	 * Determines if an event is recent based on the time difference threshold.
	 * @param difference - The time difference in seconds
	 * @returns True if the event occurred within the recent threshold
	 */
	const isRecentEvent = (difference: number): boolean => {
		return difference < RECENT_EVENT_THRESHOLD_SECONDS;
	};

	/**
	 * Gets the appropriate color based on whether an event is recent.
	 * @param difference - The time difference in seconds
	 * @returns 'primary' for recent events, 'default' otherwise
	 */
	const getEventColor = (difference: number): DotsPropsTypes['color'] => {
		return isRecentEvent(difference) ? 'primary' : 'default';
	};

	/**
	 * Updates the visual indicators based on recent button press and rotation events.
	 * Highlights indicators in primary color if the event occurred within the last 10 seconds.
	 */
	beforeUpdate(() => {
		currentTimestamp = Math.floor(Date.now() / MILLISECONDS_TO_SECONDS);

		buttonEvents.forEach((event, index) => {
			if (index < MAX_BUTTON_COUNT) {
				const difference = calculateTimeDifference(event.last_ev_ts, currentTimestamp);
				buttonColors[index] = getEventColor(difference);
			}
		});

		if (rotationData) {
			const difference = calculateTimeDifference(rotationData.last_ev_ts, currentTimestamp);
			rotationColor = getEventColor(difference);
		}
	});
</script>

<Stack gap="xs" align="center" direction={rotaryOnly ? 'row' : 'column'}>
	{#if buttonEvents[0] && !rotaryOnly}
		{#if useV2Components}
			<LabelV2
				icon="fas fa-hand-back-point-up"
				dotsType={mappedInputReaderEvent[buttonEvents[0].last_ev] || 'one'}
				dotsColor={buttonColors[0]}
				{...labelV2Props}
			/>
		{:else}
			<Label
				icon="fas fa-hand-back-point-up"
				dotsType={mappedInputReaderEvent[buttonEvents[0].last_ev] || 'one'}
				dotsColor={buttonColors[0]}
				size="l"
			/>
		{/if}
	{/if}

	{#if rotationData || rotaryOnly}
		<RotationIndicator
			rotationInput={rotationData}
			color={rotationColor}
			orientation={rotaryOnly ? 'horizontal' : 'vertical'}
			alwaysShow={rotaryOnly}
			isCompact={rotaryOnly}
		/>
	{/if}

	{#if buttonEvents[1] && !rotaryOnly}
		{#if useV2Components}
			<LabelV2
				icon="fas fa-hand-back-point-up"
				dotsType={mappedInputReaderEvent[buttonEvents[1].last_ev] || 'one'}
				dotsColor={buttonColors[1]}
				{...labelV2Props}
			/>
		{:else}
			<Label
				icon="fas fa-hand-back-point-up"
				dotsType={mappedInputReaderEvent[buttonEvents[1].last_ev] || 'one'}
				dotsColor={buttonColors[1]}
				size="l"
			/>
		{/if}
	{/if}
</Stack>
